import PropTypes from "prop-types";
import React, { useState, useEffect, useCallback } from "react";
import { Container, Row, Col, Input, Label } from "reactstrap";
import { Link } from "react-router-dom";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { DataTable, IconAvatar } from "../../../components/Common/PageWidgets";
import CustomInput from "../../../components/AuthenticationComponents/CustomInput";
import CustomSelector from "../../../components/AuthenticationComponents/CustomSelector";
import CustomMultiSelector from "../../../components/AuthenticationComponents/CustomMultiSelector";
import SecondaryButton from "../../../components/Common/SecondaryButton";
import ActivityDetailModal from "../../../components/Common/ActivityDetailModal";


//Import API
import endpoints from "../../../api/endpoints";
import { get } from "../../../api/axios";

//Utils
import { extractErrorMessage } from "../../../utils/errorHandler";

//Toast notifications
import toastr from "toastr";
import "toastr/build/toastr.min.css";


import whiteArrow from "../../../assets/images/user-dashboard/white-right-arrow.svg";
import eyeIcon from "../../../assets/images/support/eye.svg";
import coinIcon from "../../../assets/images/user-dashboard/voucher.svg";
import starIcon from "../../../assets/images/user-dashboard/star.svg";
import thumbPlaceholder from "../../../assets/images/user-dashboard/thumb-placeholder.svg";

// Validation schema using Yup
const restaurantProfileSchema = Yup.object().shape({
  title: Yup.string()
    .required("Restaurant name is required")
    .min(2, "Restaurant name must be at least 2 characters")
    .max(255, "Restaurant name must not exceed 255 characters"),
  legal_business_name: Yup.string()
    .max(255, "Legal business name must not exceed 255 characters"),
  email: Yup.string().required("Email is required").email("Please enter a valid email address"),
  mobile: Yup.string()
    .required("Phone number is required")
    .min(10, "Phone number must be at least 10 characters"),
  overview: Yup.string()
    .required("Overview is required")
    .min(10, "Overview must be at least 10 characters"),
  price_range: Yup.string().required("Price range is required"),
  address: Yup.string().required("Address is required"),
});

/* -------------------------------------------------------------------------
 * RestaurantProfilePage — restaurant profile summary + activity history.
 * ---------------------------------------------------------------------- */

const stripHtmlTags = (html) => {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
};

const cleanActivityText = (value, fallback = "") => {
  const text = stripHtmlTags(value).trim();
  if (!text || text.toLowerCase() === "default" || text.includes("App\\Models")) return fallback;
  return text;
};

const ACTIVITY_ICON = {
  api_login: { icon: starIcon, tone: "teal" },
  default: { icon: coinIcon, tone: "blue" },
};

const getActivityIcon = (activity) => ACTIVITY_ICON[activity?.event] || ACTIVITY_ICON.default;

const getActivityRows = (responseData) => {
  const payload = responseData?.data || responseData || {};
  const activitiesPayload = payload.activities || payload;

  if (Array.isArray(activitiesPayload)) return activitiesPayload;
  if (Array.isArray(activitiesPayload?.data)) return activitiesPayload.data;
  if (Array.isArray(payload?.data)) return payload.data;

  return [];
};

const activityColumns = (onView) => [
  {
    key: "description",
    label: "Activity",
    width: "3fr",
    render: (row) => {
      const { icon, tone } = getActivityIcon(row);
      return (
        <span className="data-table__row-label" style={{ whiteSpace: 'normal', lineHeight: '1.4' }}>
          <IconAvatar icon={icon} tone={tone} size={32} />
          <span style={{ wordWrap: 'break-word', overflowWrap: 'break-word' }}>
            {cleanActivityText(row.description, "Activity recorded")}
          </span>
        </span>
      );
    },
  },
  {
    key: "type",
    label: "Type",
    width: "1fr",
    render: (row) => cleanActivityText(row.type, "Activity"),
  },
  {
    key: "relative_date",
    label: "Date",
    width: "1fr",
    render: (row) => row.date || "Unknown",
  },
  {
    key: "view",
    label: "",
    width: "64px",
    align: "center",
    render: (row) => (
      <button
        type="button"
        onClick={() => onView && onView(row.id)}
        className="view-activity-btn"
        aria-label={`View ${cleanActivityText(row.type, "activity")}`}
      >
        <img src={eyeIcon} alt="" className="view-activity-btn__icon" />
      </button>
    ),
  },
];

const RestaurantProfilePage = ({ onOpenSettings, onViewActivity }) => {
  document.title = "Restaurant Profile | Omaha Food Lovers";

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [initialValues, setInitialValues] = useState({
    title: "",
    legal_business_name: "",
    email: "",
    mobile: "",
    overview: "",
    price_range: "$$",
    address: "",
  });
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [hasMoreCategories, setHasMoreCategories] = useState(true);
  const [categoryPage, setCategoryPage] = useState(1);
  const [bannerPreview, setBannerPreview] = useState(null);
  const [weekHours, setWeekHours] = useState([
    { day: 'Monday', open: '', closed: '', isClosed: false, id: null },
    { day: 'Tuesday', open: '', closed: '', isClosed: false, id: null },
    { day: 'Wednesday', open: '', closed: '', isClosed: false, id: null },
    { day: 'Thursday', open: '', closed: '', isClosed: false, id: null },
    { day: 'Friday', open: '', closed: '', isClosed: false, id: null },
    { day: 'Saturday', open: '', closed: '', isClosed: false, id: null },
    { day: 'Sunday', open: '', closed: '', isClosed: false, id: null },
  ]);
  const [isOpen24Hours, setIsOpen24Hours] = useState(false);
  const [staffInitialValues, setStaffInitialValues] = useState({
    first_name: "",
    last_name: "",
    email: "",
  });
  const addressInputRef = React.useRef(null);
  const autocompleteRef = React.useRef(null);
  const formikRef = React.useRef(null);

  // Load Google Places API script
  useEffect(() => {
    const apiKey = process.env.REACT_APP_GOOGLE_PLACES_API_KEY;

    if (!window.google && apiKey) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
      script.async = true;
      script.onerror = () => {
        toastr.error('Failed to load Google Places API script');
      };

      window.gm_authFailure = () => {
        toastr.error('Google Maps API authentication failed. Please check your API key.');
      };

      document.body.appendChild(script);
    } else if (!apiKey) {
      toastr.error('Google Places API key is missing. Please add REACT_APP_GOOGLE_PLACES_API_KEY to .env file');
    }
  }, []);

  // Initialize Google Places Autocomplete
  useEffect(() => {
    const initAutocomplete = () => {
      if (window.google && window.google.maps && window.google.maps.places && addressInputRef.current) {
        if (autocompleteRef.current) {
          window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
        }

        const autocomplete = new window.google.maps.places.Autocomplete(
          addressInputRef.current,
          {
            types: ['establishment', 'geocode'],
            fields: ['formatted_address', 'address_components', 'geometry']
          }
        );

        autocompleteRef.current = autocomplete;

        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace();
          if (place && place.formatted_address) {
            formikRef.current?.setFieldValue('address', place.formatted_address);
          }
        });
      }
    };

    const timer = setTimeout(() => {
      if (window.google && window.google.maps && window.google.maps.places) {
        initAutocomplete();
      } else {
        const checkGoogleLoaded = setInterval(() => {
          if (window.google && window.google.maps && window.google.maps.places) {
            clearInterval(checkGoogleLoaded);
            initAutocomplete();
          }
        }, 100);

        return () => clearInterval(checkGoogleLoaded);
      }
    }, 500);

    return () => {
      clearTimeout(timer);
      if (autocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, []);

  // Fetch categories for multi-select with pagination
  const fetchCategories = async (pageNum = 1, isLoadMore = false) => {
    if (isLoadMore) {
      setLoadingCategories(true);
    } else {
      setLoadingCategories(true);
    }

    try {
      const response = await get(`${endpoints.listingCategories}?page=${pageNum}&per_page=20`);
      if (response.data?.data) {
        const data = response.data.data;
        const rawCategories = data.data || [];

        const categoryOptions = rawCategories.map((category) => ({
          value: category.id,
          label: category.title || `Category ${category.id}`,
        }));

        if (isLoadMore) {
          setCategories((prev) => [...prev, ...categoryOptions]);
        } else {
          setCategories(categoryOptions);
        }

        setHasMoreCategories(data.current_page < data.last_page);
        setCategoryPage(pageNum);
      }
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleLoadMoreCategories = () => {
    if (!loadingCategories && hasMoreCategories) {
      fetchCategories(categoryPage + 1, true);
    }
  };

  // Configure toastr options
  toastr.options = {
    positionClass: "toast-bottom-left",
    timeOut: 5000,
    closeButton: true,
    progressBar: true,
  };

  // Fetch restaurant profile from API
  const fetchProfile = useCallback(async () => {
    try {
      const response = await get(endpoints.restaurantMe);

      if (response.data.success) {
        const data = response.data.data;
        const user = data.user;
        const restaurant = data.current_restaurant;

        // Set staff profile values from user data
        setStaffInitialValues({
          first_name: String(user?.first_name || ""),
          last_name: String(user?.last_name || ""),
          email: String(user?.email || ""),
        });

        const categoryIds = restaurant?.categories?.map((cat) => cat.id) || [];
        setSelectedCategories(categoryIds);

        setBannerPreview(restaurant?.banner_url || null);

        if (restaurant?.listing_time && Array.isArray(restaurant.listing_time)) {
          const dayMap = {
            'monday': 'Monday',
            'tuesday': 'Tuesday',
            'wednesday': 'Wednesday',
            'thursday': 'Thursday',
            'friday': 'Friday',
            'saturday': 'Saturday',
            'sunday': 'Sunday'
          };

          const initialWeekHours = [
            { day: 'Monday', open: '', closed: '', isClosed: false, id: null },
            { day: 'Tuesday', open: '', closed: '', isClosed: false, id: null },
            { day: 'Wednesday', open: '', closed: '', isClosed: false, id: null },
            { day: 'Thursday', open: '', closed: '', isClosed: false, id: null },
            { day: 'Friday', open: '', closed: '', isClosed: false, id: null },
            { day: 'Saturday', open: '', closed: '', isClosed: false, id: null },
            { day: 'Sunday', open: '', closed: '', isClosed: false, id: null },
          ];

          const mappedHours = initialWeekHours.map(h => {
            const apiHour = restaurant.listing_time.find(ah => {
              const mappedDay = dayMap[ah.day?.toLowerCase()];
              return mappedDay === h.day;
            });
            if (apiHour) {
              if (apiHour.is_open_24_hours === 1) {
                return { ...h, open: '00:00', closed: '23:59', isClosed: false, id: apiHour.id };
              }
              if (apiHour.hours && apiHour.hours !== 'null-null' && apiHour.hours !== null) {
                const [open, closed] = apiHour.hours.split('-');
                return { ...h, open: open?.trim() || '', closed: closed?.trim() || '', isClosed: !open || !closed, id: apiHour.id };
              }
              return { ...h, open: '', closed: '', isClosed: true, id: apiHour.id };
            }
            return h;
          });

          setWeekHours(mappedHours);

          // Use the open_24_hours field from API response
          setIsOpen24Hours(restaurant?.open_24_hours === 1);
        }

        setInitialValues({
          title: String(restaurant?.title || ""),
          legal_business_name: String(restaurant?.legal_business_name || ""),
          email: String(user.email || ""),
          mobile: String(restaurant?.mobile || ""),
          overview: String(restaurant?.overview || ""),
          price_range: String(restaurant?.price_range || "$$"),
          address: String(restaurant?.address?.address || ""),
        });
      }
    } catch (err) {
      console.error("Failed to fetch profile:", err);
      const errorMessage = extractErrorMessage(err);
      toastr.error(errorMessage);
    }
  }, []);

  // Fetch activities from API
  const fetchActivities = useCallback(async () => {
    setLoadingActivities(true);
    try {
      const response = await get(`${endpoints.restaurantDashboardActivity}?page=1&per_page=10`);

      if (response.data.success) {
        const newActivities = getActivityRows(response.data);

        setActivities(newActivities.slice(0, 10));
      }
    } catch (err) {
      console.error("Failed to fetch activities:", err);
      const errorMessage = extractErrorMessage(err);
      toastr.error(errorMessage);
      setActivities([]);
    } finally {
      setLoadingActivities(false);
    }
  }, []);

  // Fetch activities on mount
  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  // Fetch profile on mount
  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // Fetch categories on mount
  useEffect(() => {
    fetchCategories();
  }, []);

  const handleViewActivity = (activityId) => {
    const act = activities.find(a => a.id === activityId);
    if (act) {
      const { icon } = getActivityIcon(act);
      setSelectedActivity({ ...act, icon });
      setIsModalOpen(true);
    }
    if (onViewActivity) onViewActivity(activityId);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedActivity(null);
  };

  return (
    <div className="page-content profile-page">
      <Container fluid>
        
        {/* Profile Settings Section */}
        <div className="surface-card mb-4">
          <div className="profile-header">
            <div>
              <h2 className="profile-title">Profile Settings</h2>
              <p className="rp-card__subtitle" style={{ margin: 0 }}>Your profile information.</p>
            </div>
            <Link to="/restaurant/settings">
              <SecondaryButton label="Profile Settings" variant="orange" icon={whiteArrow} />
            </Link>
          </div>
          
          <div className="profile-layout">
            <div className="fields-grid profile-fields">
              <div>
                <CustomInput
                  label="First Name"
                  name="first_name"
                  type="text"
                  value={staffInitialValues.first_name}
                  className="white-bg"
                  disabled
                />
              </div>
              <div>
                <CustomInput
                  label="Last Name"
                  name="last_name"
                  type="text"
                  value={staffInitialValues.last_name}
                  className="white-bg"
                  disabled
                />
              </div>
              <div>
                <CustomInput
                  label="Email"
                  name="email"
                  type="email"
                  value={staffInitialValues.email}
                  className="white-bg"
                  disabled
                />
              </div>
            </div>
          </div>
        </div>

        <div className="surface-card profile-top-card">
          <div className="profile-header">
            <div>
              <h2 className="profile-title">Restaurant Profile</h2>
              <p className="rp-card__subtitle" style={{ margin: 0 }}>Overview of restaurant information and settings.</p>
            </div>
          </div>
          <Formik
            initialValues={initialValues}
            validationSchema={restaurantProfileSchema}
            onSubmit={() => {}}
            enableReinitialize={true}
            innerRef={formikRef}
          >
            {({ values, errors, touched, handleChange, handleBlur, isSubmitting, setFieldValue }) => (
              <Form>
                <Row>
                  <Col xl={12} lg={12} md={12} sm={12}>
                    <div className="mb-4">
                      <label className="form-label">Banner Image</label>
                      <div
                        className="rounded d-flex align-items-center justify-content-center position-relative"
                        style={{
                          width: "100%",
                          height: 200,
                          backgroundColor: "#f8f9fa",
                          border: "2px dashed #dee2e6",
                          cursor: "default",
                          overflow: "hidden",
                        }}
                      >
                        {bannerPreview ? (
                          <img
                            src={bannerPreview}
                            alt="Banner Preview"
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                            onError={(e) => {
                              e.target.src = thumbPlaceholder;
                            }}
                          />
                        ) : (
                          <div className="text-center text-muted">
                            <div style={{ fontSize: "40px", marginBottom: "10px" }}>+</div>
                            <div>No banner image</div>
                          </div>
                        )}
                      </div>
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col xl={6} lg={6} md={12} sm={12}>
                    <CustomInput
                      id="title"
                      label="Restaurant Name"
                      name="title"
                      type="text"
                      placeholder="Enter restaurant name"
                      value={values.title}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      invalid={touched.title && !!errors.title}
                      errorText={errors.title}
                      className="white-bg"
                      disabled
                    />
                  </Col>
                  <Col xl={6} lg={6} md={12} sm={12}>
                    <CustomInput
                      id="legal_business_name"
                      label="Legal Business Name"
                      name="legal_business_name"
                      type="text"
                      placeholder="Enter legal business name"
                      value={values.legal_business_name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      invalid={touched.legal_business_name && !!errors.legal_business_name}
                      errorText={errors.legal_business_name}
                      className="white-bg"
                      disabled
                    />
                  </Col>
                </Row>

                <Row>
                  <Col xl={6} lg={6} md={12} sm={12}>
                    <CustomInput
                      id="email"
                      label="Email"
                      name="email"
                      type="email"
                      placeholder="Enter email address"
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      invalid={touched.email && !!errors.email}
                      errorText={errors.email}
                      className="white-bg"
                      disabled
                    />
                  </Col>
                  <Col xl={6} lg={6} md={12} sm={12}>
                    <CustomInput
                      id="mobile"
                      label="Phone Number"
                      name="mobile"
                      type="text"
                      placeholder="Enter phone number"
                      value={values.mobile}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      invalid={touched.mobile && !!errors.mobile}
                      errorText={errors.mobile}
                      className="white-bg"
                      disabled
                    />
                  </Col>
                </Row>

                <Row>
                  <Col xl={6} lg={6} md={12} sm={12}>
                    <CustomSelector
                      id="price_range"
                      label="Price Range"
                      name="price_range"
                      placeholder="Select price range"
                      options={[
                        { value: "$", label: "$" },
                        { value: "$$", label: "$$" },
                        { value: "$$$", label: "$$$" },
                        { value: "$$$$", label: "$$$$" },
                      ]}
                      value={values.price_range}
                      onChange={(value) => setFieldValue("price_range", value)}
                      onBlur={handleBlur}
                      invalid={touched.price_range && !!errors.price_range}
                      errorText={errors.price_range}
                      className="white-bg"
                      disabled
                    />
                  </Col>
                </Row>

                <Row>
                  <Col xl={12} lg={12} md={12} sm={12}>
                    <CustomInput
                      id="overview"
                      label="Overview"
                      name="overview"
                      type="textarea"
                      placeholder="Enter restaurant overview"
                      value={values.overview}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      invalid={touched.overview && !!errors.overview}
                      errorText={errors.overview}
                      className="white-bg"
                      rows={4}
                      disabled
                    />
                  </Col>
                </Row>

                <Row>
                  <Col xl={12} lg={12} md={12} sm={12}>
                    <CustomMultiSelector
                      id="categories"
                      label="Categories"
                      name="categories"
                      placeholder="Search and select categories"
                      options={categories}
                      value={selectedCategories}
                      onChange={setSelectedCategories}
                      loading={loadingCategories}
                      onLoadMore={handleLoadMoreCategories}
                      hasMore={hasMoreCategories}
                      className="white-bg"
                      disabled
                    />
                  </Col>
                </Row>

                <Row>
                  <Col xl={12} lg={12} md={12} sm={12}>
                    <div className="custom-input-wrapper white-bg">
                      <div className={`custom-input-group ${touched.address && errors.address ? 'is-invalid' : ''}`}>
                        <Label htmlFor="address" className="custom-input-label">Address</Label>
                        <Input
                          id="address"
                          name="address"
                          type="text"
                          placeholder="Enter address (Google Places Autocomplete)"
                          value={values.address}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className="custom-input-field"
                          innerRef={addressInputRef}
                          disabled
                        />
                      </div>
                      {touched.address && errors.address && (
                        <div className="custom-input-feedback show">{errors.address}</div>
                      )}
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col xl={12} lg={12} md={12} sm={12}>
                    <div className="custom-input-wrapper white-bg">
                      <div className="custom-input-group">
                        <Label className="custom-input-label">Operating Hours</Label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 0' }}>
                          <div
                            style={{
                              width: '44px',
                              height: '24px',
                              backgroundColor: isOpen24Hours ? '#ec421c' : '#dee2e6',
                              borderRadius: '12px',
                              position: 'relative',
                              cursor: 'not-allowed',
                              transition: 'background-color 0.2s'
                            }}
                          >
                            <div
                              style={{
                                width: '20px',
                                height: '20px',
                                backgroundColor: '#fff',
                                borderRadius: '50%',
                                position: 'absolute',
                                top: '2px',
                                left: isOpen24Hours ? '22px' : '2px',
                                transition: 'left 0.2s'
                              }}
                            />
                          </div>
                          <span style={{ fontWeight: '500' }}>Open 24 Hours</span>
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>

                {!isOpen24Hours && (
                  <Row>
                    {weekHours.map((hour, index) => (
                      <Col key={hour.day} xl={3} lg={4} md={6} sm={12}>
                        <div style={{
                          backgroundColor: '#fff',
                          border: '1px solid #e5e7eb',
                          borderRadius: '16px',
                          padding: '16px',
                          marginBottom: '16px',
                          transition: 'all 0.2s ease',
                          cursor: 'default'
                        }}
                        >
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontWeight: '600', marginBottom: '12px', fontSize: '14px' }}>{hour.day}</div>
                            <div style={{ marginBottom: '8px' }}>
                              <Input
                                type="time"
                                value={hour.open}
                                onChange={(e) => {
                                  const newHours = [...weekHours];
                                  newHours[index].open = e.target.value;
                                  setWeekHours(newHours);
                                }}
                                disabled
                                style={{
                                  width: '100%',
                                  backgroundColor: '#f8f9fa',
                                  border: '1px solid #e5e7eb',
                                  borderRadius: '12px',
                                  fontSize: '13px',
                                  transition: 'all 0.2s ease'
                                }}
                              />
                            </div>
                            <div style={{ marginBottom: '8px' }}>
                              <Input
                                type="time"
                                value={hour.closed}
                                onChange={(e) => {
                                  const newHours = [...weekHours];
                                  newHours[index].closed = e.target.value;
                                  setWeekHours(newHours);
                                }}
                                disabled
                                style={{
                                  width: '100%',
                                  backgroundColor: '#f8f9fa',
                                  border: '1px solid #e5e7eb',
                                  borderRadius: '12px',
                                  fontSize: '13px',
                                  transition: 'all 0.2s ease'
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                )}
              </Form>
            )}
          </Formik>
        </div>

        <div className="wallet-section-header">
          <h2 className="wallet-section-header__title">Activity Log</h2>
          <Link to="/restaurant/profile/activity-logs">
            <SecondaryButton label="View All" variant="orange" icon={whiteArrow} />
          </Link>
        </div>

        {loadingActivities ? (
          <DataTable columns={activityColumns(handleViewActivity)} rows={[]} getRowKey={(row) => row.id} loading={true} />
        ) : activities && activities.length > 0 ? (
          <DataTable
            columns={activityColumns(handleViewActivity)}
            rows={activities.slice(0, 10)}
            getRowKey={(row) => row.id}
          />
        ) : (
          <p className="vouchers-page__empty">No recent activity yet.</p>
        )}
      </Container>

      <ActivityDetailModal
        activity={selectedActivity}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </div>
  );
};

RestaurantProfilePage.propTypes = {
  onOpenSettings: PropTypes.func,
  onViewActivity: PropTypes.func,
};

RestaurantProfilePage.defaultProps = {
  onOpenSettings: () => {},
  onViewActivity: () => {},
};

export default RestaurantProfilePage;
